import React, { useState, useEffect } from "react";
import { Plus, Printer } from "lucide-react";
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox } from "../components/UI";
import { FilterBar, buildQS, initFilters } from "../components/FilterBar";
import Badge from "../components/Badge";
import { fmtRp, fmtDate } from "../utils/format";
import { pengeluaranAPI, masterAPI, paymentAPI } from "../utils/api";
import { printBKU } from "../utils/print";

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

const PengeluaranPage = () => {
  const [data, setData]             = useState([]);
  const [filters, setFilters]       = useState(initFilters());
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch]        = useState("");
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]        = useState(false);
  const [err, setErr]              = useState("");
  const [showModal, setShowModal]  = useState(false);
  const [totalPosting, setTotalPosting] = useState(0);
  const [form, setForm] = useState({ tanggal:"", payment_request_id:"", akun_id:"", coa_detail_id:"", sumber_dana_id:"", vendor_id:"", rekening_id:"", jumlah:"", keterangan:"", metode_bayar:"TRANSFER", ref_bank:"" });
  const [refVendor, setRefVendor]   = useState([]);
  const [refAkun, setRefAkun]      = useState([]);
  const [refPayment, setRefPayment] = useState([]);
  const [refSumberDana, setRefSumberDana] = useState([]);
  const [refCoaDetail, setRefCoaDetail] = useState([]);
  const [refRekening, setRefRekening] = useState([]);
  const [paymentList, setPaymentList] = useState([]);

  const load = async (f = filters, s = search, sf = statusFilter) => {
    setLoading(true); setErr("");
    try {
      const res = await pengeluaranAPI.getAll(buildQS(f, s, sf ? { status: sf } : {}));
      setData(res.data || []);
      setTotalPosting(res.extra?.total_terposting || 0);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const loadRefs = async () => {
    try {
      const [v, a, p, sd, cd, rk] = await Promise.all([
        masterAPI.getVendor(),
        masterAPI.getCoaAkun(),
        paymentAPI.getAll('?status=DISETUJUI'),
        masterAPI.getSumberDana(),
        masterAPI.getCoaDetailAkun(),
        masterAPI.getRekening(),
      ]);
      setRefVendor((v.data||[]).filter(x=>x.status==="AKTIF").map(x=>({ value:x.id, label:x.nama })));
      setRefAkun((a.data||[]).map(x=>({
        value: x.id,
        label: `${x.kode} - ${x.nama}`,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
      })));
      setRefPayment((p.data||[]).map(x=>({ value:x.id, label:`${x.no_spp} - ${x.vendor || '-'} - ${fmtRp(x.jumlah)}` })));
      setPaymentList(p.data||[]);
      setRefSumberDana((sd.data||[]).map(x=>({ value:x.id, label:x.nama })));
      setRefCoaDetail((cd.data||[]).map(x=>({ value:x.id, label:`${x.kode} - ${x.nama}`, akun_id:x.akun_id })));
      setRefRekening((rk.data||[]).map(x=>({ value:x.id, label:`${x.nama_bank} - ${x.no_rekening}` })));
    } catch {}
  };

  const handlePaymentSelect = (prId) => {
    const pr = paymentList.find(x=>x.id===prId);
    if (pr) {
      setForm(f=>({...f,
        payment_request_id: prId,
        akun_id: f.akun_id || pr.akun_id || '',
        vendor_id: f.vendor_id || pr.vendor_id || '',
        jumlah: f.jumlah || pr.jumlah || '',
      }));
    }
  };

  useEffect(() => { load(filters, search, statusFilter); }, [filters, search, statusFilter]);
  useEffect(() => { loadRefs(); }, []);

  const handleSave = async () => {
    if (!form.tanggal || !form.akun_id || !form.jumlah || !form.keterangan) return;
    setSaving(true);
    try {
      await pengeluaranAPI.create({ ...form, jumlah: Number(form.jumlah), tahun_anggaran: Number(filters.tahun) });
      setShowModal(false);
      setForm({ tanggal:"", payment_request_id:"", akun_id:"", coa_detail_id:"", sumber_dana_id:"", vendor_id:"", rekening_id:"", jumlah:"", keterangan:"", metode_bayar:"TRANSFER", ref_bank:"" });
      load();
    } catch(e) { alert("Gagal menyimpan: " + e.message); }
    finally { setSaving(false); }
  };

  const handleCetakBKU = async () => {
    try {
      const qs = `?tahun=${filters.tahun}${filters.dari?`&dari=${filters.dari}`:''}${filters.sampai?`&sampai=${filters.sampai}`:''}`;
      const res = await pengeluaranAPI.getBku(qs);
      printBKU(res.data || [], { tahun: filters.tahun, dari: filters.dari, sampai: filters.sampai });
    } catch(e) { alert("Gagal cetak BKU: " + e.message); }
  };

  const handlePosting = async (id) => {
    if (!window.confirm("Posting pengeluaran ini? Tidak dapat dibatalkan.")) return;
    try { await pengeluaranAPI.posting(id); load(); }
    catch(e) { alert("Gagal posting: " + e.message); }
  };

  const selectStyle = { padding:"8px 12px", border:"1.5px solid #E2E8F0", borderRadius:8, fontSize:13, outline:"none", background:"white", fontFamily:"inherit" };
  const selectedAkun = refAkun.find(x => x.value === form.akun_id);
  const filteredCoaDetail = form.akun_id ? refCoaDetail.filter(d => String(d.akun_id) === String(form.akun_id)) : [];
  const selectedCoaDetail = refCoaDetail.find(d => d.value === form.coa_detail_id);

  return (
    <div>
      <PageHeader title="Bendahara Pengeluaran" subtitle="Realisasi dan pencatatan pengeluaran"
        action={<div style={{display:"flex",gap:8}}>
          <Btn variant="secondary" icon={<Printer size={14}/>} onClick={handleCetakBKU}>Cetak BKU</Btn>
          <Btn icon={<Plus size={14}/>} onClick={()=>setShowModal(true)}>Catat Pengeluaran</Btn>
        </div>}
      />
      <FilterBar filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
        searchPlaceholder="Cari no bukti, keterangan, vendor…">
        <div>
          <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:5 }}>Status</label>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{ ...selectStyle, minWidth:130 }}>
            <option value="">Semua</option>
            <option value="DRAFT">Draft</option>
            <option value="TERPOSTING">Terposting</option>
          </select>
        </div>
      </FilterBar>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:20 }}>
        <Card style={{ borderLeft:"4px solid #DC2626", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Total Terposting</p>
          <p style={{ margin:0, fontSize:18, fontWeight:700, color:"#DC2626" }}>{fmtRp(totalPosting)}</p>
          <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>Tahun {filters.tahun}</p>
        </Card>
        <Card style={{ borderLeft:"4px solid #2563EB", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Jumlah Transaksi</p>
          <p style={{ margin:0, fontSize:18, fontWeight:700, color:"#1E40AF" }}>{data.length}</p>
        </Card>
        <Card style={{ borderLeft:"4px solid #F59E0B", padding:"14px 18px" }}>
          <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>Draft (Belum Posting)</p>
          <p style={{ margin:0, fontSize:18, fontWeight:700, color:"#D97706" }}>{data.filter(d=>d.status==="DRAFT").length}</p>
        </Card>
      </div>
      <Card>
        {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={()=>load()}/></div>}
        {loading ? <Spinner/> : (
          <Table pageSize={10} cols={[
            { key:"no_bukti",    label:"No. BKK", nowrap:true },
            { key:"tanggal",     label:"Tanggal", render:v=>fmtDate(v), nowrap:true },
            { key:"no_spp",      label:"No. SPP", render:v=>v||<span style={{color:'#9CA3AF'}}>-</span>, nowrap:true },
            { key:"program_kode", label:"Program", render:(v,r)=><ProgramBadge v={v}/> },
            { key:"kegiatan_kode", label:"Kegiatan", render:(v,r)=><KegiatanBadge v={v}/> },
            { key:"kro_kode",   label:"KRO", render:(v,r)=><KroBadge v={v}/> },
            { key:"kode_akun",   label:"Akun", render:(v,r)=>(
              <div>
                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#374151"}}>{v}</span>
                <br/><span style={{fontSize:11,color:"#6B7280"}}>{r.nama_akun}</span>
              </div>
            )},
            { key:"vendor",      label:"Vendor / Penerima" },
            { key:"sumber_dana", label:"Sumber Dana", render:v=>v||<span style={{color:"#9CA3AF"}}>-</span> },
            { key:"keterangan",  label:"Keterangan" },
            { key:"jumlah",      label:"Jumlah", align:"right",
              render:v=><span style={{fontWeight:700,fontFamily:"monospace",fontSize:12,color:"#DC2626"}}>{fmtRp(v)}</span> },
            { key:"metode_bayar",label:"Metode" },
            { key:"nama_bank", label:"Bank", render:v=>v||<span style={{color:"#9CA3AF"}}>-</span> },
            { key:"status",      label:"Status", render:v=><Badge status={v}/> },
            { key:"id", label:"Aksi", render:(id,row)=>row.status==="DRAFT"?(
              <Btn size="sm" variant="success" onClick={()=>handlePosting(id)}>Posting</Btn>
            ):null },
          ]} data={data}/>
        )}
      </Card>
      {showModal && (
        <Modal title="Catat Pengeluaran Baru" onClose={()=>setShowModal(false)}>
          <Field label="Payment Request (SPP)">
            <Select value={form.payment_request_id} onChange={e=>handlePaymentSelect(e.target.value)} options={refPayment} placeholder="Pilih SPP (opsional)…"/>
          </Field>
          <Field label="Tanggal" required><Input value={form.tanggal} onChange={e=>setForm({...form,tanggal:e.target.value})} type="date"/></Field>
          <Field label="Akun MA" required>
            <Select value={form.akun_id} onChange={e=>setForm({...form,akun_id:e.target.value, coa_detail_id:""})} options={refAkun} placeholder="Pilih akun…"/>
            {selectedAkun && (
              <div style={{ marginTop:8, padding:10, background:"#F8FAFC", borderRadius:8, fontSize:12, display:"flex", gap:6, flexWrap:"wrap" }}>
                <ProgramBadge v={selectedAkun.program_kode ? `${selectedAkun.program_kode} - ${selectedAkun.program_nama}` : null}/>
                <KegiatanBadge v={selectedAkun.kegiatan_kode ? `${selectedAkun.kegiatan_kode} - ${selectedAkun.kegiatan_nama}` : null}/>
                <KroBadge v={selectedAkun.kro_kode ? `${selectedAkun.kro_kode} - ${selectedAkun.kro_nama}` : null}/>
              </div>
            )}
          </Field>
          {form.akun_id && (
            <Field label="Detail COA" required>
              <Select value={form.coa_detail_id} onChange={e=>setForm({...form,coa_detail_id:e.target.value})} options={filteredCoaDetail} placeholder="Pilih Detail COA…"/>
              {selectedCoaDetail && (
                <div style={{ marginTop:8, padding:8, background:"#EFF6FF", borderRadius:8, fontSize:12 }}>
                  <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{selectedCoaDetail.label}</span>
                </div>
              )}
            </Field>
          )}
          <Field label="Sumber Dana">
            <Select value={form.sumber_dana_id} onChange={e=>setForm({...form,sumber_dana_id:e.target.value})} options={[{value:"",label:"-- Tidak Ada --"}, ...refSumberDana]} placeholder="Pilih sumber dana…"/>
          </Field>
          <Field label="Vendor">
            <Select value={form.vendor_id} onChange={e=>setForm({...form,vendor_id:e.target.value})} options={refVendor} placeholder="Pilih vendor…"/>
          </Field>
          <Field label="Jumlah (Rp)" required><Input value={form.jumlah} onChange={e=>setForm({...form,jumlah:e.target.value})} type="number" placeholder="0"/></Field>
          <Field label="Keterangan" required><Input value={form.keterangan} onChange={e=>setForm({...form,keterangan:e.target.value})} placeholder="Keterangan pengeluaran"/></Field>
          <Field label="Metode Bayar">
            <Select value={form.metode_bayar} onChange={e=>setForm({...form,metode_bayar:e.target.value})}
              options={["TRANSFER","TUNAI","CEK","GIRO"].map(v=>({value:v,label:v}))}/>
          </Field>
          <Field label="Rekening Bank">
            <Select value={form.rekening_id} onChange={e=>setForm({...form,rekening_id:e.target.value})} options={refRekening} placeholder="Pilih rekening bank…"/>
          </Field>
          <Field label="Ref Bank"><Input value={form.ref_bank} onChange={e=>setForm({...form,ref_bank:e.target.value})} placeholder="No. referensi transfer"/></Field>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn variant="secondary" onClick={()=>setShowModal(false)}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving?"Menyimpan…":"Simpan"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PengeluaranPage;